import React, { useState } from "react";
import { FaDownload, FaTimesCircle } from "react-icons/fa";
import "./CustomerReport.css";
import "./CustomerReportFullPage.css";
import pdfMake from "pdfmake/build/pdfmake";
import * as pdfFonts from "pdfmake/build/vfs_fonts";
import BreedBreakDown from "./BreedBreakDown";
import Dog1data from "./dog1data";
import DynamicGrid from "./DynamicGrid";
import Loading from "./loading";
import { profileData } from "../jsonObject/staticData"

import { Row, Col } from 'react-bootstrap';
import apiClient from '../service/apiClient';

pdfMake.vfs = (pdfFonts && pdfFonts.pdfMake ? pdfFonts.pdfMake.vfs : pdfFonts.vfs) || (window.pdfMake && window.pdfMake.vfs) || {};

// ---------------------------------------------------------------------------
// Product image helpers
//
// The diet images are bundled AVIF/WebP files. The API serves the build with
// express.static (send 0.18 / mime 1.6), and mime@1.x does not know the .avif
// extension, so those files are returned as "application/octet-stream". The API
// also sends "X-Content-Type-Options: nosniff", which forbids the browser from
// sniffing the real format, so an <img src> pointing at the file is refused.
// We therefore always resolve the real image type from the file name, download
// the bytes ourselves and rebuild the blob with the correct type before using
// it (either as a data URL for <img> or as a JPEG for pdfmake).
// ---------------------------------------------------------------------------
const IMAGE_MIME_BY_EXTENSION = {
    avif: 'image/avif',
    webp: 'image/webp',
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    gif: 'image/gif',
    bmp: 'image/bmp',
    svg: 'image/svg+xml'
};

// pdfmake can only embed PNG and JPEG; those two are also passed through
// untouched when an image is rasterised.
const PDF_EMBEDDABLE_MIME_TYPES = ['image/png', 'image/jpeg'];

const getImageMimeType = (source) => {
    if (!source || typeof source !== 'string') return null;
    const trimmed = source.trim();
    const dataUrlMatch = /^data:(image\/[a-z0-9.+-]+)[;,]/i.exec(trimmed);
    if (dataUrlMatch) return dataUrlMatch[1].toLowerCase();
    const withoutQuery = trimmed.split('?')[0].split('#')[0];
    const extension = withoutQuery.slice(withoutQuery.lastIndexOf('.') + 1).toLowerCase();
    return IMAGE_MIME_BY_EXTENSION[extension] || null;
};

const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
});

const blobToJpegDataUrl = (blob) => new Promise((resolve) => {
    const image = new Image();
    const objectUrl = URL.createObjectURL(blob);
    image.onload = () => {
        try {
            const canvas = document.createElement('canvas');
            canvas.width = image.naturalWidth || image.width;
            canvas.height = image.naturalHeight || image.height;
            canvas.getContext('2d').drawImage(image, 0, 0);
            resolve(canvas.toDataURL('image/jpeg', 0.92));
        } catch (error) {
            console.warn('Failed to rasterise image for the report', error);
            resolve(null);
        } finally {
            URL.revokeObjectURL(objectUrl);
        }
    };
    image.onerror = (error) => {
        URL.revokeObjectURL(objectUrl);
        console.warn('Failed to decode image for the report', error);
        resolve(null);
    };
    image.src = objectUrl;
});

// Downloads any image (bundled asset, remote URL or data URL) and returns a data
// URL that both <img> and pdfmake can render, or null when it cannot be decoded.
// With { rasterize: true } the image is re-encoded to JPEG unless it already is
// PNG/JPEG, which is what pdfmake requires.
const fetchImageAsDataUrl = async (source, { rasterize = false } = {}) => {
    if (!source || typeof source !== 'string') return null;

    const declaredMime = getImageMimeType(source);
    if (source.startsWith('data:')) {
        if (!rasterize || PDF_EMBEDDABLE_MIME_TYPES.includes(declaredMime)) return source;
    }

    try {
        const response = await fetch(source);
        if (!response.ok) {
            console.warn(`Report image request failed (${response.status}): ${source}`);
            return null;
        }
        const headerMime = (response.headers.get('content-type') || '').split(';')[0].trim().toLowerCase();
        // Prefer the file extension: the API reports .avif as application/octet-stream
        const mimeType = declaredMime || (headerMime.startsWith('image/') ? headerMime : null);
        if (!mimeType) {
            console.warn(`Report image has an unsupported type: ${source} (${headerMime || 'unknown'})`);
            return null;
        }
        const buffer = await response.arrayBuffer();
        const blob = new Blob([buffer], { type: mimeType });
        if (rasterize && !PDF_EMBEDDABLE_MIME_TYPES.includes(mimeType)) {
            return await blobToJpegDataUrl(blob);
        }
        return await blobToDataUrl(blob);
    } catch (error) {
        console.warn(`Report image could not be loaded: ${source}`, error);
        return null;
    }
};

const ScientificDetails = ({ healthTraitId, kitId }) => {
    const [geneResult, setGeneResult] = useState([]);

    React.useEffect(() => {
        if (!healthTraitId || !kitId) return;
        const fetchDetails = async () => {
            try {
                const response = await apiClient.get(`/getHealthGeneByKitId?health_trait_id=${healthTraitId}&kit_id=${kitId}`);
                if (response.data?.ItemInfo) {
                    const statusPriority = { affected: 0, carrier: 1, normal: 2 };
                    const geneData = response.data.ItemInfo
                        .filter(condition => condition.chromosome && condition.position && condition.status !== 'missing_genotype')
                        .sort((a, b) => {
                            const statusA = statusPriority[a.status] ?? 3;
                            const statusB = statusPriority[b.status] ?? 3;
                            if (statusA !== statusB) return statusA - statusB;
                            const chromA = parseInt(a.chromosome.replace('chr', '')) || 100;
                            const chromB = parseInt(b.chromosome.replace('chr', '')) || 100;
                            if (chromA !== chromB) return chromA - chromB;
                            return a.position - b.position;
                        });
                    setGeneResult(geneData);
                }
            } catch (error) {
                console.error("Error fetching scientific details", error);
            }
        };
        fetchDetails();
    }, [healthTraitId, kitId]);

    if (!geneResult || geneResult.length === 0) return null;

    return (
        <div className="condition-container" style={{ marginTop: '25px', paddingTop: '20px', borderTop: '1px dashed #eaeaea' }}>
            <h3 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1a1a1a', marginBottom: '20px' }}>
                Scientific Details
            </h3>
            <Row className="scientific-container" style={{ background: '#fff', borderRadius: '12px', padding: '15px', border: '1px solid #f0f0f0', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                <Col className="scientific-col" xs={12} md={4} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '10px 0' }}>
                    <div className="scientific-title" style={{ marginBottom: '10px', fontSize: '16px', fontWeight: 'bold', color: '#09718c', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Chromosome</div>
                    {geneResult.map((d, i) => <div key={`chr-${i}`} className="scientific-detail" style={{ fontSize: '18px', color: '#333', marginBottom: '5px', fontWeight: '600' }}>{d.chromosome}</div>)}
                </Col>
                <Col className="scientific-col" xs={12} md={4} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '10px 0' }}>
                    <div className="scientific-title" style={{ marginBottom: '10px', fontSize: '16px', fontWeight: 'bold', color: '#09718c', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Position</div>
                    {geneResult.map((d, i) => <div key={`pos-${i}`} className="scientific-detail" style={{ fontSize: '18px', color: '#333', marginBottom: '5px', fontWeight: '600' }}>{d.position}</div>)}
                </Col>
                <Col className="scientific-col" style={{ borderRight: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '10px 0' }} xs={12} md={4}>
                    <div className="scientific-title" style={{ marginBottom: '10px', fontSize: '16px', fontWeight: 'bold', color: '#09718c', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Status</div>
                    {geneResult.map((d, i) => <div key={`sts-${i}`} className="scientific-detail" style={{ fontSize: '18px', color: '#333', marginBottom: '5px', fontWeight: '600' }}>{d.status.charAt(0).toUpperCase() + d.status.slice(1)}</div>)}
                </Col>
            </Row>
        </div>
    );
};

const References = ({ healthTraitId }) => {
    const [referenceResult, setReferenceResult] = useState([]);

    React.useEffect(() => {
        if (!healthTraitId) return;
        const fetchDetails = async () => {
            try {
                const response = await apiClient.get(`/getHealthReferenceByHealthTrait?health_trait_id=${healthTraitId}`);
                if (response.data?.ItemInfo) {
                    const uniqueRefs = [];
                    const seen = new Set();
                    response.data.ItemInfo.forEach(item => {
                        if (item.reference && !seen.has(item.reference)) {
                            seen.add(item.reference);
                            uniqueRefs.push(item);
                        }
                    });
                    setReferenceResult(uniqueRefs);
                }
            } catch (error) {
                if (error?.response?.status !== 404) {
                    console.error("Error fetching references", error);
                }
            }
        };
        fetchDetails();
    }, [healthTraitId]);

    if (!referenceResult || referenceResult.length === 0) return null;

    return (
        <div className="condition-container" style={{ marginTop: '25px', paddingTop: '20px', borderTop: '1px dashed #eaeaea' }}>
            <h3 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1a1a1a', marginBottom: '15px' }}>
                References
            </h3>
            <ul style={{ paddingLeft: '20px', margin: 0 }}>
                {referenceResult.map((ref, index) => (
                    <li key={index} className="reference-item" style={{ fontSize: '14px', color: '#666', marginBottom: '8px', lineHeight: '1.5' }}>
                        {ref?.reference}
                    </li>
                ))}
            </ul>
        </div>
    );
};

function CustomerReportFullPage({ surveyData, years, filteredData, healthInfo, dietInfo, petImage }) {
    const [loading, setLoading] = useState(false);

    // Raw base64 payloads are stored without their mime type, so sniff the magic bytes.
    // This keeps pdfmake happy (it only renders PNG/JPEG and relies on the data URL prefix).
    const detectBase64MimeType = (base64) => {
        if (/^\/9j\//.test(base64)) return "image/jpeg";
        if (/^iVBORw0KGgo/.test(base64)) return "image/png";
        if (/^R0lGOD/.test(base64)) return "image/gif";
        if (/^UklGR/.test(base64)) return "image/webp";
        if (/^AAAAIGZ0eXBhdm/.test(base64)) return "image/avif";
        return "image/png";
    };

    // The pet photo can arrive as a raw base64 string, a data URL or an http(s) URL.
    // Normalize it into a source that <img> and pdfmake can consume.
    const getPetImageSrc = (imageData) => {
        if (!imageData || typeof imageData !== "string" || imageData === "NONE") return null;
        const value = imageData.trim();
        if (value.startsWith("data:image")) return value;
        if (value.startsWith("http://") || value.startsWith("https://")) return value;
        if (value.startsWith("/")) return value;
        return `data:${detectBase64MimeType(value)};base64,${value}`;
    };

    const petImageSrc = getPetImageSrc(petImage) || getPetImageSrc(surveyData?.imgBase64);

    let requireContext;
    try {
        requireContext = require.context(
            "../asset/dietImages",
            false,
            /\.(avif|png|jpe?g|gif|webp|svg)$/
        );
    } catch (e) {
        console.warn("Failed to load dietImages context", e);
    }

    const getImage = (imageName) => {
        if (!imageName || imageName === "NONE") return null;
        if (imageName.startsWith("http://") || imageName.startsWith("https://")) {
            return imageName;
        }
        if (!requireContext) return null;
        try {
            const resolved = requireContext(`./${imageName}`);
            if (typeof resolved === "string") return resolved;
            // Webpack asset modules can expose the URL on the default export
            return resolved && typeof resolved.default === "string" ? resolved.default : null;
        } catch (error) {
            return null;
        }
    };

    // Diet images are served as application/octet-stream (.avif + mime@1.x) with
    // X-Content-Type-Options: nosniff, so the browser refuses to render them when
    // they are used directly as an <img src>. On error we fetch the bytes and swap
    // in a data URL that carries the correct mime type, which renders everywhere.
    const [productImageDataUrls, setProductImageDataUrls] = useState({});
    const [failedProductImages, setFailedProductImages] = useState({});

    const handleProductImageError = (imageName) => async () => {
        if (failedProductImages[imageName]) return;
        if (productImageDataUrls[imageName]) {
            // The converted data URL failed as well, show the placeholder instead
            setFailedProductImages((previous) => ({ ...previous, [imageName]: true }));
            return;
        }
        // Rasterising to JPEG makes the fallback render in every browser, including
        // ones that cannot decode AVIF themselves.
        const dataUrl = await fetchImageAsDataUrl(getImage(imageName), { rasterize: true });
        if (dataUrl) {
            setProductImageDataUrls((previous) => ({ ...previous, [imageName]: dataUrl }));
        } else {
            setFailedProductImages((previous) => ({ ...previous, [imageName]: true }));
        }
    };

    const renderProductImage = (imageName, productName) => {
        const source = productImageDataUrls[imageName] || getImage(imageName);
        if (!source || failedProductImages[imageName]) {
            if (!source) {
                // Surfaced so a product_image value that does not match any bundled
                // file in src/asset/dietImages is easy to spot in the console.
                console.warn(`Report product image not found in the bundle: "${imageName}"`);
            }
            return <div className="product-image-placeholder">Image</div>;
        }
        return (
            <img
                src={source}
                alt={productName}
                className="product-rec-image"
                onError={handleProductImageError(imageName)}
            />
        );
    };

    const getCopyBadgeLabel = (status) => {
        if (!status) return 'Unknown';
        const normalized = String(status).toLowerCase();
        if (normalized === 'affected') return 'Two Copies';
        if (normalized === 'carrier') return 'One Copy';
        return 'Normal';
    };

    const prioritizeHealthTraits = (data) => {
        if (!data) return [];
        const statusPriority = { affected: 1, carrier: 2, normal: 3 };
        const traitMap = {};
        data.forEach(item => {
            const { health_trait_name, status } = item;
            const normalizedStatus = status ? status.toLowerCase() : '';
            if (!traitMap[health_trait_name]) {
                traitMap[health_trait_name] = item;
            } else {
                const newPriority = statusPriority[normalizedStatus] ?? 99;
                const existingStatus = traitMap[health_trait_name].status ? traitMap[health_trait_name].status.toLowerCase() : '';
                const existingPriority = statusPriority[existingStatus] ?? 99;
                if (newPriority < existingPriority) {
                    traitMap[health_trait_name] = item;
                }
            }
        });
        return Object.values(traitMap);
    };

    const handleDownloadPdf = async () => {
        setLoading(true);

        try {
            const content = [];

            // Kept as a thin wrapper so every PDF call site converts images the same way
            // (correct mime type from the file name, WebP/AVIF rasterised to JPEG).
            const getBase64ImageFromUrl = (imageUrl) => fetchImageAsDataUrl(imageUrl, { rasterize: true });

            const createCard = (title, badgeText, badgeType, details) => {
                const leftBorderColor = badgeType === 'high' ? '#09718c' : badgeType === 'abnormal' ? '#cc0000' : '#e0e0e0';
                const badgeColor = badgeType === 'high' ? '#09718c' : badgeType === 'abnormal' ? '#cc0000' : '#555555';
                const badgeBg = badgeType === 'high' ? '#e6f1f4' : badgeType === 'abnormal' ? '#fae5e5' : '#f0f0f0';

                const detailsStack = details.map(d => ({
                    text: [
                        { text: `${d.label}: `, bold: true, color: '#333' },
                        { text: d.value, color: '#555' }
                    ],
                    margin: [0, 0, 0, 6],
                    fontSize: 11
                }));

                return {
                    table: {
                        widths: ['*', 'auto'],
                        body: [
                            [
                                { text: title, fontSize: 14, bold: true, color: '#222' },
                                {
                                    text: badgeText ? `  ${badgeText}  ` : '',
                                    fontSize: 10,
                                    bold: true,
                                    background: badgeText ? badgeBg : null,
                                    color: badgeColor,
                                    alignment: 'right'
                                }
                            ],
                            [
                                {
                                    stack: detailsStack,
                                    colSpan: 2
                                },
                                {}
                            ]
                        ]
                    },
                    layout: {
                        paddingLeft: function (i) { return 15; },
                        paddingRight: function (i) { return 15; },
                        paddingTop: function (i) { return (i === 0) ? 15 : 5; },
                        paddingBottom: function (i) { return (i === 1) ? 15 : 5; },
                        vLineWidth: function (i) { return (i === 0) ? 4 : 1; },
                        hLineWidth: function (i) { return 1; },
                        vLineColor: function (i) { return (i === 0) ? leftBorderColor : '#f0f0f0'; },
                        hLineColor: function (i) { return '#f0f0f0'; }
                    },
                    margin: [0, 0, 0, 15]
                };
            };

            const createRecommendationCard = (name, type, link, imageBase64) => {
                const imageContent = imageBase64
                    ? { image: imageBase64, fit: [50, 50], alignment: 'center', margin: [0, 5, 0, 5] }
                    : {
                        text: 'Image',
                        fillColor: '#f5f7fa',
                        color: '#666',
                        fontSize: 10,
                        bold: true,
                        alignment: 'center',
                        margin: [0, 15, 0, 15]
                    };

                return {
                    table: {
                        widths: [60, '*'],
                        body: [
                            [
                                imageContent,
                                {
                                    stack: [
                                        { text: name, fontSize: 14, bold: true, color: '#09718c', link: link !== "NONE" ? link : null, decoration: link !== "NONE" ? 'underline' : null },
                                        { text: type.toUpperCase(), fontSize: 10, color: '#888', margin: [0, 4, 0, 0] }
                                    ],
                                    margin: [15, 10, 0, 0]
                                }
                            ]
                        ]
                    },
                    layout: {
                        paddingLeft: function (i) { return 0; },
                        paddingRight: function (i) { return 15; },
                        paddingTop: function (i) { return 0; },
                        paddingBottom: function (i) { return 0; },
                        vLineWidth: function (i) { return 1; },
                        hLineWidth: function (i) { return 1; },
                        vLineColor: function (i) { return '#f0f0f0'; },
                        hLineColor: function (i) { return '#f0f0f0'; }
                    },
                    margin: [0, 0, 0, 15]
                };
            };

            const addSectionHeader = (title, pageBreak = false) => {
                const headerObj = { text: title, style: 'sectionHeader' };
                if (pageBreak) {
                    headerObj.pageBreak = 'before';
                }
                content.push(headerObj);
                content.push({ canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 2, lineColor: '#09718c' }], margin: [0, -10, 0, 20] });
            };

            // Title
            content.push({ text: `${surveyData?.PetName || 'Pet'}'s Profile`, style: 'header' });

            // Profile Data
            const breedString = Array.isArray(surveyData?.BreedType) && surveyData.BreedType.length > 0
                ? surveyData.BreedType.join(", ")
                : surveyData?.BreedType || "Chihuahua mix";
            const supplementsString = Array.isArray(surveyData?.ConsumingSupplements) && surveyData.ConsumingSupplements.length > 0
                ? surveyData.ConsumingSupplements.join(", ")
                : surveyData?.ConsumingSupplements || "None";

            const profileDetails = [
                { text: [{ text: 'Breed: ', bold: true }, breedString], style: 'profileItem' },
                { text: [{ text: 'Sex: ', bold: true }, surveyData?.Gender || ''], style: 'profileItem' },
                { text: [{ text: 'Age: ', bold: true }, `${years} years`], style: 'profileItem' },
                { text: [{ text: 'Weight: ', bold: true }, `${surveyData?.PetWeightInPounds || ''} lbs`], style: 'profileItem' },
                { text: [{ text: 'Medication/Supplements: ', bold: true }, supplementsString], style: 'profileItem' },
                { text: [{ text: 'Veterinary Diet: ', bold: true }, 'No'], style: 'profileItem' },
                { text: [{ text: 'Kit ID: ', bold: true }, surveyData?.Kit_id || ''], style: 'profileItem' },
                { text: [{ text: 'Pet Parent: ', bold: true }, surveyData?.Name || ''], style: 'profileItem' }
            ];

            // Pet photo (left) rendered next to the profile details (right)
            const petImageBase64 = petImageSrc ? await getBase64ImageFromUrl(petImageSrc) : null;

            if (petImageBase64) {
                content.push({
                    table: {
                        widths: [150, '*'],
                        body: [
                            [
                                { image: petImageBase64, fit: [140, 140], alignment: 'center', margin: [0, 8, 0, 8] },
                                { stack: profileDetails, margin: [20, 12, 0, 0] }
                            ]
                        ]
                    },
                    layout: 'noBorders',
                    margin: [0, 5, 0, 15]
                });
            } else {
                content.push(...profileDetails);
            }

            content.push({ text: '', margin: [0, 20, 0, 0] }); // Spacer

            // Nutrition Insights
            const validNutritionInsights = Object.keys(filteredData || {})
                .filter(key => key !== 'foodrecommendations' && key !== 'supplementrecommendations')
                .map(key => filteredData[key])
                .filter(c =>
                    c.DisplayName?.[0]?.result1_value && c.DisplayName[0].result1_value !== 'N/A' &&
                    c.Results?.[0]?.result1_value && c.Results[0].result1_value !== 'N/A' && c.Results[0].result1_value.trim() !== ''
                );

            if (validNutritionInsights.length > 0) {
                addSectionHeader('Nutrition Insights', true);
                validNutritionInsights.forEach(c => {
                    const isHigh = c.Results?.[0]?.variable1_value?.toLowerCase() === 'high';
                    const details = [{ label: 'Result', value: c.Results[0].result1_value }];
                    if (c.Description?.[0]?.result1_value) {
                        details.push({ label: 'Description', value: c.Description[0].result1_value });
                    }
                    if (isHigh && c.Recommendations?.[0]?.result1_value) {
                        details.push({ label: 'Recommendation', value: c.Recommendations[0].result1_value });
                    }

                    content.push(createCard(
                        c.DisplayName[0].result1_value,
                        isHigh ? 'High' : '',
                        isHigh ? 'high' : 'normal',
                        details
                    ));
                });
            }

            // Health Insights
            const deduplicatedHealthInfoPdf = prioritizeHealthTraits(healthInfo?.ItemInfo);
            const validHealthInsights = deduplicatedHealthInfoPdf.filter(h => h.health_trait_name && h.status && h.status.toLowerCase() !== 'missing_genotype' && h.status.toLowerCase() !== 'normal');
            if (validHealthInsights.length > 0) {
                addSectionHeader('Health Insights', true);
                for (const h of validHealthInsights) {
                    const isAbnormal = h.status.toLowerCase() !== 'normal';
                    const details = [];
                    if (h.sub_health_category) {
                        details.push({ label: 'Category', value: h.sub_health_category });
                    }
                    if (h.age_of_onset && h.age_of_onset !== "NONE" && h.age_of_onset.trim() !== "") {
                        details.push({ label: 'Age of Onset', value: h.age_of_onset.replace(/\${CHAR\(10\)}/g, '') });
                    }
                    if (h.prevelance && h.prevelance !== "NONE" && h.prevelance.trim() !== "") {
                        details.push({ label: 'Prevalence', value: h.prevelance.replace(/\${CHAR\(10\)}/g, '') });
                    }
                    if (h.condition_details) {
                        details.push({ label: 'Details', value: h.condition_details });
                    }

                    // Fetch Scientific Details
                    let scientificText = [];
                    try {
                        const geneRes = await apiClient.get(`/getHealthGeneByKitId?health_trait_id=${h.health_trait_id}&kit_id=${surveyData?.Kit_id}`);
                        if (geneRes.data?.ItemInfo && geneRes.data.ItemInfo.length > 0) {
                            const statusPriority = { affected: 0, carrier: 1, normal: 2 };
                            const geneData = geneRes.data.ItemInfo
                                .filter(condition => condition.chromosome && condition.position && condition.status !== 'missing_genotype')
                                .sort((a, b) => {
                                    const statusA = statusPriority[a.status] ?? 3;
                                    const statusB = statusPriority[b.status] ?? 3;
                                    if (statusA !== statusB) return statusA - statusB;
                                    const chromA = parseInt(a.chromosome.replace('chr', '')) || 100;
                                    const chromB = parseInt(b.chromosome.replace('chr', '')) || 100;
                                    if (chromA !== chromB) return chromA - chromB;
                                    return a.position - b.position;
                                });

                            if (geneData.length > 0) {
                                scientificText = geneData.map(g => `Chr ${g.chromosome}, Pos ${g.position} - ${g.status.charAt(0).toUpperCase() + g.status.slice(1)}`);
                            }
                        }
                    } catch (e) {
                        console.error("Error fetching scientific details for PDF", e);
                    }

                    if (scientificText.length > 0) {
                        details.push({ label: 'Scientific Details', value: scientificText.join(' | ') });
                    }

                    // Fetch References
                    let refText = [];
                    try {
                        const refRes = await apiClient.get(`/getHealthReferenceByHealthTrait?health_trait_id=${h.health_trait_id}`);
                        if (refRes.data?.ItemInfo && refRes.data.ItemInfo.length > 0) {
                            const uniqueRefs = [];
                            const seen = new Set();
                            refRes.data.ItemInfo.forEach(item => {
                                if (item.reference && !seen.has(item.reference)) {
                                    seen.add(item.reference);
                                    uniqueRefs.push(item.reference);
                                }
                            });
                            refText = uniqueRefs;
                        }
                    } catch (e) {
                        // ignore 404
                    }

                    if (refText.length > 0) {
                        details.push({ label: 'References', value: refText.join('\n\n') });
                    }

                    content.push(createCard(
                        h.health_trait_name,
                        getCopyBadgeLabel(h.status),
                        isAbnormal ? 'abnormal' : 'normal',
                        details
                    ));
                }
            }

            // Diet & Supplement Recommendation
            const validFood = dietInfo?.ItemInfo?.filter(d =>
                d.product_type === "food" &&
                d.product_id && d.product_id !== "NONE" &&
                d.product_image && d.product_image !== "NONE" &&
                d.product_name && d.product_name !== "NONE" &&
                d.product_link && d.product_link !== "NONE"
            ) || [];

            const validSupplement = dietInfo?.ItemInfo?.filter(d =>
                d.product_type === "supplement" &&
                d.product_id && d.product_id !== "NONE" &&
                d.product_image && d.product_image !== "NONE" &&
                d.product_name && d.product_name !== "NONE" &&
                d.product_link && d.product_link !== "NONE"
            ) || [];

            const petDisplayName = surveyData?.PetName || 'your dog';

            addSectionHeader('Diet & Supplement Recommendation', true);

            if (validFood.length > 0) {
                content.push({ text: 'Food Recommendations', style: 'subHeader' });
                content.push({
                    text: `Based on ${petDisplayName}’s DNA, we’ve analyzed specific nutrition traits that may influence your dog’s unique dietary needs. These insights help us identify areas where ${petDisplayName} could be at risk for nutrient gaps. To help meet ${petDisplayName}’s needs, we’ve provided a selection of food options that align with these genetic insights and support overall health. We recommend consulting your veterinarian to determine the best choice for ${petDisplayName}.`,
                    style: 'paragraph'
                });
                for (let i = 0; i < validFood.length; i++) {
                    const d = validFood[i];
                    const imgUrl = getImage(d.product_image);
                    if (!imgUrl) console.warn(`Report product image not found in the bundle: "${d.product_image}"`);
                    const base64 = imgUrl ? await getBase64ImageFromUrl(imgUrl) : null;
                    content.push(createRecommendationCard(`#${i + 1}  ${d.product_name}`, d.product_type, d.product_link, base64));
                }
            }

            if (validSupplement.length > 0) {
                content.push({ text: 'Nutritional and Supplements Recommendations', style: 'subHeader' });
                content.push({
                    text: `Based on ${petDisplayName}’s DNA nutrition traits and current life stage, we’ve identified targeted supplements that can help support areas where your dog may have increased nutritional needs. These recommendations are designed to complement ${petDisplayName}’s diet and promote overall health. For the best results, we suggest reviewing these options with your veterinarian.`,
                    style: 'paragraph'
                });
                for (let i = 0; i < validSupplement.length; i++) {
                    const d = validSupplement[i];
                    const imgUrl = getImage(d.product_image);
                    if (!imgUrl) console.warn(`Report product image not found in the bundle: "${d.product_image}"`);
                    const base64 = imgUrl ? await getBase64ImageFromUrl(imgUrl) : null;
                    content.push(createRecommendationCard(`#${i + 1}  ${d.product_name}`, d.product_type, d.product_link, base64));
                }
            }

            if (validFood.length === 0 && validSupplement.length === 0) {
                content.push({ text: 'No diet or supplement recommendations available.', style: 'itemDetail', margin: [0, 0, 0, 20] });
            }

            const docDefinition = {
                content: content,
                defaultStyle: {
                    font: 'Roboto'
                },
                styles: {
                    header: {
                        fontSize: 26,
                        bold: true,
                        margin: [0, 0, 0, 20],
                        color: '#1a1a1a'
                    },
                    sectionHeader: {
                        fontSize: 20,
                        bold: true,
                        margin: [0, 20, 0, 15],
                        color: '#1a1a1a'
                    },
                    subHeader: {
                        fontSize: 16,
                        bold: true,
                        margin: [0, 10, 0, 10],
                        color: '#09718c'
                    },
                    paragraph: {
                        fontSize: 12,
                        lineHeight: 1.4,
                        color: '#555',
                        margin: [0, 0, 0, 15]
                    },
                    profileItem: {
                        fontSize: 12,
                        margin: [0, 2, 0, 2],
                        color: '#333'
                    },
                    itemTitle: {
                        fontSize: 14,
                        bold: true,
                        color: '#222',
                        margin: [0, 5, 0, 2]
                    },
                    itemDetail: {
                        fontSize: 11,
                        color: '#555',
                        margin: [0, 1, 0, 1]
                    },
                    itemDetailType: {
                        fontSize: 10,
                        color: '#888',
                        margin: [0, 1, 0, 1]
                    }
                }
            };

            const customerName = surveyData?.Name ? surveyData.Name.replace(/\s+/g, '_') : 'Customer';
            const petName = surveyData?.PetName ? surveyData.PetName.replace(/\s+/g, '_') : 'Pet';
            pdfMake.createPdf(docDefinition).download(`${customerName}_${petName}_DNA_Report.pdf`);
        } catch (error) {
            console.error("Print error:", error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            {loading && <Loading message="Loading report data..." />}
            <div className="download-pdf-btn-container">
                <button
                    onClick={handleDownloadPdf}
                    className="download-pdf-btn"
                    disabled={loading}
                >
                    <FaDownload />
                    {loading ? "Generating PDF..." : "Download PDF"}
                </button>
            </div>
            <div className="full-page-report-container">
                <section className="profile-section report-section">
                    <h3 className="section-heading-premium">
                        {surveyData?.PetName}'s Profile
                    </h3>
                    <div className="dog-profile-container">
                        <div className="dog-details">
                            <div className="dog-profile">
                                {petImageSrc ? (
                                    <img
                                        src={petImageSrc}
                                        alt={surveyData?.PetName || "Pet"}
                                        className="pet-profile-image"
                                    />
                                ) : (
                                    <div className="pet-profile-placeholder">
                                        No photo available
                                    </div>
                                )}
                            </div>
                            <div className="dog-profile-info">
                                <div className="mb-2">
                                    <label className="fw-bold me-2">Breed:</label>
                                    <span>
                                        {Array.isArray(surveyData?.BreedType) &&
                                            surveyData.BreedType.length > 0
                                            ? surveyData.BreedType.join(", ")
                                            : surveyData?.BreedType || "Chihuahua mix"}
                                    </span>
                                </div>
                                <div className="mb-2">
                                    <label className="fw-bold me-2">Sex:</label>
                                    <span>{surveyData?.Gender}</span>
                                </div>
                                <div className="mb-2">
                                    <label className="fw-bold me-2">Age:</label>
                                    <span>{years} years</span>
                                </div>
                                <div className="mb-2">
                                    <label className="fw-bold me-2">Weight:</label>
                                    <span>{surveyData?.PetWeightInPounds} lbs</span>
                                </div>
                                <div className="mb-2">
                                    <label className="fw-bold me-2">
                                        Medication/Supplements:
                                    </label>
                                    <span>
                                        {Array.isArray(surveyData?.ConsumingSupplements) &&
                                            surveyData.ConsumingSupplements.length > 0
                                            ? surveyData.ConsumingSupplements.join(", ")
                                            : surveyData?.ConsumingSupplements || "None"}
                                    </span>
                                </div>
                                <div className="mb-2">
                                    <label className="fw-bold me-2">Veterinary Diet:</label>
                                    <span>No</span>
                                </div>
                                <hr style={{ width: "90%" }} />
                                <div className="mb-2">
                                    <label className="fw-bold me-2">Kit ID:</label>
                                    <span>{surveyData?.Kit_id}</span>
                                </div>
                                <div className="mb-2">
                                    <label className="fw-bold me-2">Pet Parent:</label>
                                    <span>{surveyData?.Name}</span>
                                </div>
                            </div>
                            <div className="dog-profile-info">
                                <div className="mb-2">
                                    {profileData.profileSection.map((para, i) => (
                                        <p key={i}>{para}</p>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <div className="contentReport">
                    {(() => {
                        const validNutritionInsights = Object.keys(filteredData || {})
                            .filter(key => key !== 'foodrecommendations' && key !== 'supplementrecommendations')
                            .map(key => filteredData[key])
                            .filter(c =>
                                c.DisplayName?.[0]?.result1_value && c.DisplayName[0].result1_value !== 'N/A' &&
                                c.Results?.[0]?.result1_value && c.Results[0].result1_value !== 'N/A' && c.Results[0].result1_value.trim() !== ''
                            );

                        if (validNutritionInsights.length === 0) return null;

                        return (
                            <div className="report-section">
                                <h3 className="section-heading-premium">Nutrition Insights</h3>
                                <ul className="premium-list">
                                    {validNutritionInsights.map((c, i) => {
                                        const isHigh = c.Results?.[0]?.variable1_value?.toLowerCase() === 'high';
                                        return (
                                            <li key={i} className={`premium-list-item ${isHigh ? 'high-risk' : ''}`}>
                                                <div className="premium-item-title">
                                                    {c.DisplayName[0].result1_value}
                                                    {isHigh && <span className="premium-badge high">High</span>}
                                                </div>
                                                <div className="premium-item-detail"><strong>Result:</strong> {c.Results[0].result1_value}</div>
                                                {c.Description?.[0]?.result1_value && (
                                                    <div className="premium-item-detail"><strong>Description:</strong> {c.Description[0].result1_value}</div>
                                                )}
                                                {isHigh && c.Recommendations?.[0]?.result1_value && (
                                                    <div className="premium-item-detail"><strong>Recommendation:</strong> {c.Recommendations[0].result1_value}</div>
                                                )}
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        );
                    })()}

                    {(() => {
                        const deduplicatedHealthInfo = prioritizeHealthTraits(healthInfo?.ItemInfo);
                        const validHealthInsights = deduplicatedHealthInfo.filter(h => h.health_trait_name && h.status && h.status.toLowerCase() !== 'missing_genotype' && h.status.toLowerCase() !== 'normal');

                        if (validHealthInsights.length === 0) return null;

                        return (
                            <div className="report-section">
                                <h3 className="section-heading-premium">Health Insights</h3>
                                <ul className="premium-list">
                                    {validHealthInsights.map((h, i) => {
                                        const isAbnormal = h.status.toLowerCase() !== 'normal';
                                        return (
                                            <li key={i} className={`premium-list-item ${isAbnormal ? 'abnormal-risk' : ''}`} style={{ display: 'flex', flexDirection: 'column' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                                        <span className={`copy-badge ${h.status.toLowerCase() === 'affected' ? 'copy-badge--two' : 'copy-badge--one'}`}>
                                                            {getCopyBadgeLabel(h.status)}
                                                        </span>
                                                        <span style={{ fontSize: '20px', fontWeight: 'bold' }}>
                                                            {h.health_trait_name}
                                                        </span>
                                                    </div>

                                                    {(h.status.toLowerCase() === "affected" || h.status.toLowerCase() === "carrier") && (
                                                        <div style={{ display: 'flex', alignItems: 'center' }} className="trait-right">
                                                            {h.sub_health_category && (
                                                                <span className="subhealth-chip">
                                                                    {h.sub_health_category}
                                                                </span>
                                                            )}
                                                            <span className="variant-parent" style={{ background: "#cc00009e", marginLeft: "10px", padding: "5px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", width: "35px", height: "35px" }}>
                                                                <span className="xiconColor" style={{ color: 'white', display: 'flex', alignItems: 'center' }}>
                                                                    <FaTimesCircle size={24} />
                                                                </span>
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="insight-expanded-details" style={{ marginTop: '20px', borderTop: '1px solid #eaeaea', paddingTop: '20px' }}>
                                                    {h.condition_details && (
                                                        <div className="condition-container" style={{ marginBottom: '25px' }}>
                                                            <h3 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1a1a1a', marginBottom: '12px' }}>About</h3>
                                                            <div style={{
                                                                padding: "16px 20px",
                                                                margin: 0,
                                                                backgroundColor: "rgba(9, 113, 140, 0.04)",
                                                                borderLeft: "4px solid #09718c",
                                                                borderRadius: "0 8px 8px 0",
                                                                fontSize: '15px',
                                                                color: '#444',
                                                                lineHeight: '1.6'
                                                            }}>
                                                                {h.condition_details}
                                                            </div>
                                                        </div>
                                                    )}
                                                    <div className="condition-container">
                                                        <DynamicGrid items={h} />
                                                    </div>
                                                    <ScientificDetails healthTraitId={h.health_trait_id} kitId={surveyData?.Kit_id} />
                                                    <References healthTraitId={h.health_trait_id} />
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        );
                    })()}

                    {(() => {
                        const validFood = dietInfo?.ItemInfo?.filter(d =>
                            d.product_type === "food" &&
                            d.product_id && d.product_id !== "NONE" &&
                            d.product_image && d.product_image !== "NONE" &&
                            d.product_name && d.product_name !== "NONE" &&
                            d.product_link && d.product_link !== "NONE"
                        ) || [];

                        const validSupplement = dietInfo?.ItemInfo?.filter(d =>
                            d.product_type === "supplement" &&
                            d.product_id && d.product_id !== "NONE" &&
                            d.product_image && d.product_image !== "NONE" &&
                            d.product_name && d.product_name !== "NONE" &&
                            d.product_link && d.product_link !== "NONE"
                        ) || [];

                        const petName = surveyData?.PetName || 'your dog';

                        return (
                            <div className="report-section">
                                <h3 className="section-heading-premium">Diet & Supplement Recommendation</h3>

                                {validFood.length > 0 && (
                                    <div style={{ marginBottom: '40px' }}>
                                        <h4 style={{ fontSize: '18px', fontWeight: 'bold', color: '#09718c', marginBottom: '15px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Food Recommendations</h4>
                                        <p style={{ fontSize: '15px', color: '#555', lineHeight: '1.6', marginBottom: '20px', background: '#f9f9f9', padding: '15px 20px', borderRadius: '8px', borderLeft: '4px solid #09718c' }}>
                                            Based on {petName}’s DNA, we’ve analyzed specific nutrition traits that may influence your dog’s unique dietary needs. These insights help us identify areas where {petName} could be at risk for nutrient gaps. To help meet {petName}’s needs, we’ve provided a selection of food options that align with these genetic insights and support overall health. We recommend consulting your veterinarian to determine the best choice for {petName}.
                                        </p>
                                        <ul className="premium-list">
                                            {validFood.map((d, i) => (
                                                <li key={i} className="premium-list-item recommendation-item" style={{ display: 'flex', alignItems: 'center' }}>
                                                    <div style={{ marginRight: '15px', fontWeight: 'bold', fontSize: '20px', color: '#09718c' }}>#{i + 1}</div>
                                                    {renderProductImage(d.product_image, d.product_name)}
                                                    <div className="rec-content" style={{ flexGrow: 1 }}>
                                                        <a href={d.product_link} target="_blank" rel="noreferrer" className="rec-link" style={{ fontSize: '16px', fontWeight: 'bold', color: '#09718c', textDecoration: 'none', marginBottom: '8px', display: 'block' }}>
                                                            {d.product_name}
                                                        </a>
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {validSupplement.length > 0 && (
                                    <div>
                                        <h4 style={{ fontSize: '18px', fontWeight: 'bold', color: '#09718c', marginBottom: '15px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Nutritional and Supplements Recommendations</h4>
                                        <p style={{ fontSize: '15px', color: '#555', lineHeight: '1.6', marginBottom: '20px', background: '#f9f9f9', padding: '15px 20px', borderRadius: '8px', borderLeft: '4px solid #09718c' }}>
                                            Based on {petName}’s DNA nutrition traits and current life stage, we’ve identified targeted supplements that can help support areas where your dog may have increased nutritional needs. These recommendations are designed to complement {petName}’s diet and promote overall health. For the best results, we suggest reviewing these options with your veterinarian.
                                        </p>
                                        <ul className="premium-list">
                                            {validSupplement.map((d, i) => (
                                                <li key={i} className="premium-list-item recommendation-item" style={{ display: 'flex', alignItems: 'center' }}>
                                                    <div style={{ marginRight: '15px', fontWeight: 'bold', fontSize: '20px', color: '#09718c' }}>#{i + 1}</div>
                                                    {renderProductImage(d.product_image, d.product_name)}
                                                    <div className="rec-content" style={{ flexGrow: 1 }}>
                                                        <a href={d.product_link} target="_blank" rel="noreferrer" className="rec-link" style={{ fontSize: '16px', fontWeight: 'bold', color: '#09718c', textDecoration: 'none', marginBottom: '8px', display: 'block' }}>
                                                            {d.product_name}
                                                        </a>
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {validFood.length === 0 && validSupplement.length === 0 && (
                                    <div style={{ color: '#666', fontStyle: 'italic', textAlign: 'center', padding: '40px' }}>
                                        No diet or supplement recommendations available.
                                    </div>
                                )}
                            </div>
                        );
                    })()}

                    {filteredData?.breedInformation && (
                        <div className="report-section">
                            <h3 className="section-heading-premium">Breed Information</h3>
                            <BreedBreakDown ruleResult={filteredData} />
                        </div>
                    )}

                    {filteredData?.lifestyleSuggestions && (
                        <div className="report-section">
                            <h3 className="section-heading-premium">Lifestyle Suggestions</h3>
                            <Dog1data
                                ruleResult={filteredData}
                                kitData={surveyData}
                            />
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}

export default CustomerReportFullPage;